import React, { useState, useCallback, useRef, useMemo } from 'react';
import { AppInput, AppAlert, AppAutocomplete } from '@pro-sto/ui';
import { adminSearchClients, adminSearchCars } from '@pro-sto/api-client';
import type { SelectOption as AutocompleteOption } from '@pro-sto/ui';
import type { ClientSearchResultDto, CarSearchResultDto } from '@pro-sto/api-client';
import { useStores } from '@/hooks';
import { formatPhoneWithMask, formatClientName } from '../utils';

export interface ClientPhoneFieldProps {
  /** Текущий номер телефона */
  phone: string;
  /** Имя клиента */
  clientName: string;
  /** Callback при изменении телефона */
  onPhoneChange: (phone: string) => void;
  /** Callback при изменении имени */
  onClientNameChange: (name: string) => void;
  /** Выбранный клиент из автокомплита (null если новый) */
  selectedClient: ClientSearchResultDto | null;
  /** Callback при выборе клиента */
  onClientSelect: (client: ClientSearchResultDto | null) => void;
  /** Callback когда загружены машины клиента */
  onCarsLoaded: (options: AutocompleteOption[]) => void;
  /** Поля в режиме только чтения */
  disabled?: boolean;
  /** Обязательное поле */
  required?: boolean;
  /** Текст ошибки для поля телефона */
  error?: string;
  /** Callback для сброса ошибки при изменении значения */
  onErrorClear?: () => void;
}

export const ClientPhoneField: React.FC<ClientPhoneFieldProps> = ({
  phone,
  clientName,
  onPhoneChange,
  onClientNameChange,
  selectedClient,
  onClientSelect,
  onCarsLoaded,
  disabled = false,
  required = false,
  error,
  onErrorClear,
}) => {
  const { toastStore } = useStores();

  const [nameConflict, setNameConflict] = useState<{ dbName: string; enteredName: string } | null>(null);
  const userEnteredName = useRef<string>('');

  const phoneAutocompleteValue = useMemo<AutocompleteOption>(() => ({
    label: phone || '+7 ',
    value: selectedClient?.uuid || null,
    isCustom: !selectedClient,
  }), [phone, selectedClient]);

  // Поиск клиентов по телефону
  const searchClients = useCallback(async (phoneQuery: string): Promise<AutocompleteOption[]> => {
    const digits = phoneQuery.replace(/\D/g, '');
    const searchDigits = digits.startsWith('7') ? digits.substring(1) : digits;

    if (searchDigits.length < 3) return [];

    try {
      const results = await adminSearchClients({ phone: searchDigits, limit: 10 });
      return results.map((client: ClientSearchResultDto) => ({
        label: formatPhoneWithMask(client.phone),
        value: client.uuid,
        isCustom: false,
        rawData: client,
      } as AutocompleteOption & { rawData: ClientSearchResultDto }));
    } catch (error) {
      console.error('Failed to search clients:', error);
      toastStore.showError('Не удалось выполнить поиск клиентов');
      return [];
    }
  }, [toastStore]);

  const applyPhoneValue = useCallback((nextPhone: string) => {
    onPhoneChange(nextPhone);
    onErrorClear?.();
  }, [onErrorClear, onPhoneChange]);

  // Выбор клиента из автокомплита
  const handleClientSelect = useCallback(async (option: AutocompleteOption) => {
    if (option.isCustom || !option.value) {
      applyPhoneValue(option.label);
      onClientSelect(null);
      return;
    }

    const clientData = (option as AutocompleteOption & { rawData: ClientSearchResultDto }).rawData;

    if (clientData) {
      const dbName = formatClientName(clientData.name);
      const enteredName = userEnteredName.current.trim();

      if (enteredName && dbName && enteredName.toLowerCase() !== dbName.toLowerCase()) {
        setNameConflict({ dbName, enteredName });
      } else {
        onClientNameChange(dbName);
      }

      onClientSelect(clientData);
      applyPhoneValue(formatPhoneWithMask(clientData.phone));

      // Загрузка автомобилей выбранного клиента
      try {
        const cars = await adminSearchCars({
          licensePlate: '',
          clientUuid: clientData.uuid,
          limit: 50,
        });

        const carsOptions = cars.map((car: CarSearchResultDto) => ({
          label: car.license_plate,
          value: car.uuid,
          isCustom: false,
          rawData: car,
        } as AutocompleteOption & { rawData: CarSearchResultDto }));

        onCarsLoaded(carsOptions);
      } catch (error) {
        console.error('Failed to load client cars:', error);
        onCarsLoaded([]);
      }
    }
  }, [applyPhoneValue, onClientSelect, onClientNameChange, onCarsLoaded]);

  const handleReplaceNameWithDb = useCallback(() => {
    if (nameConflict) {
      onClientNameChange(nameConflict.dbName);
      setNameConflict(null);
    }
  }, [nameConflict, onClientNameChange]);

  const handleKeepEnteredName = useCallback(() => {
    setNameConflict(null);
  }, []);

  // Приём значения от маски (IMask сам управляет курсором и ограничивает длину ввода —
  // раньше здесь была ручная математика курсора, которая ломалась и позволяла бесконечно
  // печатать в конце номера).
  // ВАЖНО: не зовём onPhoneChange здесь — это создаёт цикл ре-рендеров через родителя,
  // который сбивает внутренний DOM-стейт IMask. Итоговое значение уходит наверх через
  // handleClientSelect при закрытии автокомплита (blur/клик вне поля), как и раньше.
  const handlePhoneAccept = useCallback(() => {
    onErrorClear?.();
  }, [onErrorClear]);

  return (
    <>
      <div className="booking-form__field-row">
        <div className="booking-form__field" data-testid="booking-client-phone-input">
          <AppAutocomplete
            label="Номер телефона"
            placeholder="+7 (000) 000 00 00"
            value={phoneAutocompleteValue}
            onSearch={disabled ? undefined : searchClients}
            onChange={disabled ? undefined : handleClientSelect}
            minSearchLength={3}
            searchDebounce={300}
            disabled={disabled}
            required={required}
            error={error}
            renderOption={(option) => {
              const clientData = (option as AutocompleteOption & { rawData?: ClientSearchResultDto }).rawData;
              const optionName = formatClientName(clientData?.name);
              if (optionName) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', pointerEvents: 'none' }}>
                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{optionName}</div>
                  </div>
                );
              }
              return option.label;
            }}
            mask="+{7} (000) 000-00-00"
            lazy={false}
            placeholderChar="_"
            onAccept={disabled ? undefined : handlePhoneAccept}
          />
        </div>

        <div className="booking-form__field" data-testid="booking-client-name-input">
          <AppInput
            label="Имя клиента"
            placeholder="Имя клиента"
            value={clientName}
            onChange={(value) => {
              onClientNameChange(value);
              userEnteredName.current = value;
            }}
            disabled={disabled}
          />
        </div>
      </div>

      {!disabled && nameConflict && (
        <AppAlert
          message={`Мы нашли пользователя в базе с таким номером, но с другим именем. Заменить на "${nameConflict.dbName}"?`}
          onConfirm={handleReplaceNameWithDb}
          onCancel={handleKeepEnteredName}
          confirmText="Заменить"
          cancelText="Оставить"
        />
      )}
    </>
  );
};
